import { Route } from 'react-router-dom';
import { RiSunLine, RiMoonLine } from '@remixicon/react';
import { UnifiedThemeProvider } from '@backstage/theme';
import { customLightTheme, customDarkTheme } from './theme';
import { apiDocsPlugin, ApiExplorerPage } from '@backstage/plugin-api-docs';
import {
  CatalogEntityPage,
  CatalogIndexPage,
  catalogPlugin,
  CatalogTable,
} from '@backstage/plugin-catalog';
import {
  CatalogImportPage,
  catalogImportPlugin,
} from '@backstage/plugin-catalog-import';
import { ScaffolderPage, scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { orgPlugin } from '@backstage/plugin-org';
import { SearchPage } from '@backstage/plugin-search';
import { CopilotIndexPage } from '@backstage-community/plugin-copilot';
import {
  TechDocsIndexPage,
  techdocsPlugin,
  TechDocsReaderPage,
  EntityListDocsTable,
} from '@backstage/plugin-techdocs';
import { Link } from '@backstage/core-components';
import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { ReportIssue } from '@backstage/plugin-techdocs-module-addons-contrib';
import { UserSettingsPage } from '@backstage/plugin-user-settings';
import { apis } from './apis';
import { entityPage } from './components/catalog/EntityPage';
import { searchPage } from './components/search/SearchPage';
import { Root } from './components/Root';
import { HomePage } from './components/HomePage';
import { AlertDisplay, OAuthRequestDialog } from '@backstage/core-components';
import { createApp } from '@backstage/app-defaults';
import { AppRouter, FlatRoutes } from '@backstage/core-app-api';
import { CatalogGraphPage } from '@backstage/plugin-catalog-graph';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';
import { CatalogUnprocessedEntitiesPage } from '@backstage/plugin-catalog-unprocessed-entities';
import { microsoftAuthApiRef } from '@backstage/core-plugin-api';
import { SignInPage } from '@backstage/core-components';
import { EmisxReleasesPage } from '@internal/backstage-plugin-emisx-releases';
import { AppExtensionsPage } from '@internal/backstage-plugin-app-extensions';
import { configApiRef, useApi } from '@backstage/core-plugin-api';
import { doraMetricsPlugin } from '@jikwan/backstage-plugin-dora-scorecard';
import { techInsightsPlugin } from '@backstage-community/plugin-tech-insights';
import {
  emisTechInsightsPlugin,
  TechInsightsDashboard,
} from '@internal/backstage-plugin-emis-tech-insights';

const app = createApp({
  components: {
    SignInPage: props => (
      <SignInPage
        {...props}
        providers={
          useApi(configApiRef).getString('auth.environment') === 'development'
            ? ['guest']
            : [
                {
                  id: 'custom-azure-auth-provider',
                  title: 'Microsoft',
                  message: 'Sign in using SSO',
                  apiRef: microsoftAuthApiRef,
                },
              ]
        }
        title="Select a sign-in method"
        align="center"
      />
    ),
  },
  apis,
  bindRoutes({ bind }) {
    bind(catalogPlugin.externalRoutes, {
      createComponent: scaffolderPlugin.routes.root,
      viewTechDoc: techdocsPlugin.routes.docRoot,
      createFromTemplate: scaffolderPlugin.routes.selectedTemplate,
    });
    bind(apiDocsPlugin.externalRoutes, {
      registerApi: catalogImportPlugin.routes.importPage,
    });
    bind(scaffolderPlugin.externalRoutes, {
      registerComponent: catalogImportPlugin.routes.importPage,
      viewTechDoc: techdocsPlugin.routes.docRoot,
    });
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex,
    });
  },
  themes: [
    {
      id: 'light',
      title: 'Light',
      variant: 'light',
      icon: <RiSunLine />,
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={customLightTheme} children={children} />
      ),
    },
    {
      id: 'dark',
      title: 'Dark',
      variant: 'dark',
      icon: <RiMoonLine />,
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={customDarkTheme} children={children} />
      ),
    },
  ],
  plugins: [doraMetricsPlugin, techInsightsPlugin, emisTechInsightsPlugin],
});

const routes = (
  <FlatRoutes>
    <Route path="/" element={<HomePage />} />
    <Route
      path="/catalog"
      element={
        <CatalogIndexPage
          columns={[
            CatalogTable.columns.createNameColumn(),
            CatalogTable.columns.createOwnerColumn(),
            CatalogTable.columns.createSpecTypeColumn(),
            CatalogTable.columns.createSpecLifecycleColumn(),
          ]}
          initiallySelectedFilter="all"
          ownerPickerMode="all"
        />
      }
    />
    <Route
      path="/catalog/:namespace/:kind/:name"
      element={<CatalogEntityPage />}
    >
      {entityPage}
    </Route>
    <Route
      path="/docs"
      element={
        <TechDocsIndexPage
          columns={[
            EntityListDocsTable.columns.createTitleColumn({ hidden: true }),
            {
              title: 'Document',
              field: 'entity.metadata.name',
              highlight: true,
              searchable: true,
              defaultSort: 'asc',
              render: (row: any) => (
                <Link to={row.resolved.docsUrl}>
                  {row.entity.metadata.title || row.entity.metadata.name}
                </Link>
              ),
            },
            EntityListDocsTable.columns.createOwnerColumn(),
            EntityListDocsTable.columns.createKindColumn(),
            EntityListDocsTable.columns.createTypeColumn(),
          ]}
          options={{ padding: 'dense' }}
        />
      }
    />
    <Route
      path="/docs/:namespace/:kind/:name/*"
      element={<TechDocsReaderPage />}
    >
      <TechDocsAddons>
        <ReportIssue />
      </TechDocsAddons>
    </Route>
    <Route
      path="/create"
      element={
        <ScaffolderPage
          groups={[
            {
              title: 'Golden Path',
              filter: template =>
                template.metadata.tags?.includes('golden-path') ?? false,
            },
            {
              title: 'JFrog',
              filter: template =>
                template.metadata.tags?.includes('jfrog') ?? false,
            },
            {
              title: 'Dynatrace',
              filter: template =>
                template.metadata.tags?.includes('dynatrace') ?? false,
            },
          ]}
        />
      }
    />
    <Route
      path="/api-docs"
      element={
        <ApiExplorerPage
          columns={[
            CatalogTable.columns.createNameColumn(),
            CatalogTable.columns.createOwnerColumn(),
            CatalogTable.columns.createSpecTypeColumn(),
            CatalogTable.columns.createSpecLifecycleColumn(),
          ]}
        />
      }
    />
    <Route path="/copilot" element={<CopilotIndexPage />} />
    <Route
      path="/catalog-import"
      element={
        <RequirePermission permission={catalogEntityCreatePermission}>
          <CatalogImportPage />
        </RequirePermission>
      }
    />
    <Route path="/search" element={<SearchPage />}>
      {searchPage}
    </Route>
    <Route path="/settings" element={<UserSettingsPage />} />
    <Route path="/catalog-graph" element={<CatalogGraphPage />} />
    <Route path="/emisx-releases" element={<EmisxReleasesPage />} />
    <Route path="/app-extensions" element={<AppExtensionsPage />} />
    <Route
      path="/catalog-unprocessed-entities"
      element={<CatalogUnprocessedEntitiesPage />}
    />
    <Route path="/maturity" element={<TechInsightsDashboard />} />
  </FlatRoutes>
);

export default app.createRoot(
  <>
    <AlertDisplay />
    <OAuthRequestDialog />
    <AppRouter>
      <Root>{routes}</Root>
    </AppRouter>
  </>,
);
